import { apolloProvider } from '@/apollo-provider.js'
import { HOTSITE_SLOT } from '@/modules/configuracoes/site-de-cadastramento/graphql'
import cloneDeep from 'lodash/cloneDeep'
import { validateHotsiteConfiguration } from '@/modules/configuracoes/site-de-cadastramento/utils'

export default {
  async fetchHotsiteSlot({ commit, dispatch }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const { data } = await apolloProvider.defaultClient.query({
        query: HOTSITE_SLOT,
        variables: { id },
        fetchPolicy: 'no-cache',
      })

      const hotsiteSlot = cloneDeep(data.hotsiteSlot)
      commit('SET_HOTSITE_SLOT', hotsiteSlot)

      // Set configurations
      if (hotsiteSlot.production_configuration) {
        commit('SET_PRODUCTION_CONFIGURATION', hotsiteSlot.production_configuration)
      }
      if (hotsiteSlot.preview_configuration) {
        commit('SET_PREVIEW_CONFIGURATION', hotsiteSlot.preview_configuration)
      }

      // Start with production configuration by default, or use defaults if both are null
      const currentConfig = hotsiteSlot.production_configuration || hotsiteSlot.preview_configuration
      if (currentConfig) {
        dispatch('setConfigurationProperties', currentConfig)
      } else {
        // Initialize with default configuration structure when no configurations exist
        dispatch('initializeWithDefaults')
      }
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching hotsite slot:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setConfigurationProperties({ commit }, currentConfig) {
    commit('SET_CURRENT_CONFIGURATION', currentConfig)

    // Extract colors, images, positions, and overrides from current configuration or use defaults
    if (currentConfig?.settings?.assets) {
      commit('SET_COLORS', currentConfig.settings.assets.colors || {})
      commit('SET_IMAGES', currentConfig.settings.assets.images || {})
      commit('SET_POSITIONS', currentConfig.settings.assets.positions || {})
    } else {
      // No configuration exists, initialize with empty objects (defaults will be used by getters)
      commit('SET_COLORS', {})
      commit('SET_IMAGES', {})
      commit('SET_POSITIONS', {})
    }

    // Initialize fields
    if (currentConfig?.settings?.fields) {
      commit('SET_FIELDS', currentConfig.settings.fields)
    } else {
      commit('SET_FIELDS', {})
    }

    // Initialize overrides
    if (currentConfig?.overrides) {
      commit('SET_OVERRIDES', currentConfig.overrides)
    } else {
      commit('SET_OVERRIDES', {})
    }
  },

  setCurrentConfiguration({ commit, state }, configType) {
    let config = null
    if (configType === 'production') {
      config = state.productionConfiguration
    } else if (configType === 'preview') {
      config = state.previewConfiguration
    }

    if (config) {
      commit('SET_CURRENT_CONFIGURATION', config)

      // Update colors, images, positions, and overrides based on new configuration
      if (config.settings?.assets) {
        if (config.settings.assets.colors) {
          commit('SET_COLORS', config.settings.assets.colors)
        }
        if (config.settings.assets.images) {
          commit('SET_IMAGES', config.settings.assets.images)
        }
        if (config.settings.assets.positions) {
          commit('SET_POSITIONS', config.settings.assets.positions)
        }
      }

      // Update fields
      if (config.settings?.fields) {
        commit('SET_FIELDS', config.settings.fields)
      } else {
        commit('SET_FIELDS', {})
      }

      // Update overrides
      if (config.overrides) {
        commit('SET_OVERRIDES', config.overrides)
      } else {
        commit('SET_OVERRIDES', {})
      }
    }
  },

  updateColors({ commit }, colors) {
    commit('UPDATE_COLORS', colors)
  },

  updateImages({ commit }, images) {
    commit('UPDATE_IMAGES', images)
  },

  updatePositions({ commit }, positions) {
    commit('UPDATE_POSITIONS', positions)
  },

  setPositions({ commit }, positions) {
    commit('SET_POSITIONS', positions)
  },

  updateOverrides({ commit }, overrides) {
    commit('UPDATE_OVERRIDES', overrides)
  },

  setOverrides({ commit }, overrides) {
    commit('SET_OVERRIDES', overrides)
  },

  setFields({ commit }, fields) {
    commit('SET_FIELDS', fields)
  },

  updateFields({ commit }, fields) {
    commit('UPDATE_FIELDS', fields)
  },

  setError({ commit }, error) {
    commit('SET_ERROR', error)
  },

  clearError({ commit }) {
    commit('SET_ERROR', null)
  },

  clearConfiguration({ commit }) {
    commit('SET_HOTSITE_SLOT', null)
    commit('SET_PRODUCTION_CONFIGURATION', null)
    commit('SET_PREVIEW_CONFIGURATION', null)
    commit('SET_CURRENT_CONFIGURATION', {})
    commit('SET_COLORS', {})
    commit('SET_IMAGES', {})
    commit('SET_POSITIONS', {})
    commit('SET_OVERRIDES', {})
    commit('SET_ERROR', null)
  },

  validateConfiguration({ commit, getters }) {
    const configuration = getters.currentConfigurationToSave
    const errors = validateHotsiteConfiguration(configuration)
    commit('SET_VALIDATION_ERRORS', errors)
    return errors
  },

  clearValidationErrors({ commit }) {
    commit('CLEAR_VALIDATION_ERRORS')
  },

  initializeWithDefaults({ commit, state }) {
    // Create a default configuration structure using the default settings
    const defaultConfig = {
      settings: {
        assets: state.defaultConfigurationSettings.assets,
        fields: state.defaultConfigurationSettings.fields,
      },
      overrides: state.defaultConfigurationSettings.overrides,
      mode: 'NEW',
      version: 0,
    }

    commit('SET_CURRENT_CONFIGURATION', defaultConfig)

    // Initialize individual state slices with defaults
    commit('SET_COLORS', state.defaultConfigurationSettings.assets.colors)
    commit('SET_IMAGES', state.defaultConfigurationSettings.assets.images)
    commit('SET_POSITIONS', state.defaultConfigurationSettings.assets.positions)
    commit('SET_FIELDS', state.defaultConfigurationSettings.fields)
    commit('SET_OVERRIDES', state.defaultConfigurationSettings.overrides)
  },
}
